/** Тонкий типізований клієнт API. Помилки завжди приходять як ApiFailure. */

import { reads } from './inflight.js';

import { OfflineQueued, shouldQueue, useLink, type MutMethod } from '../store/link.js';

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export { OfflineQueued, isOfflineQueued } from '../store/link.js';

const jsonHeaders = { 'Content-Type': 'application/json' };

async function fetchApi(path: string, options: RequestInit): Promise<Response> {
  const mutating = options.method !== 'GET';
  if (mutating) reads.invalidate();
  try {
    return await fetch(path, options);
  } finally {
    if (mutating) reads.invalidate();
  }
}

const isRetriableHttp = (status: number): boolean =>
  status === 502 || status === 503 || status === 504;

const isNetwork = (e: unknown): boolean =>
  e instanceof TypeError ||
  (typeof DOMException !== 'undefined' &&
    e instanceof DOMException &&
    (e.name === 'AbortError' || e.name === 'TimeoutError'));

async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const isJson = (res.headers.get('content-type') ?? '').includes('json');
  const body = isJson ? await res.json() : await res.text();
  if (!res.ok) {
    const b = body as { error?: string; message?: string; details?: unknown };
    throw new ApiFailure(
      res.status,
      b?.error ?? 'error',
      b?.message ?? (typeof body === 'string' && body ? body : 'Помилка запиту'),
      b?.details,
    );
  }
  return body as T;
}

const queueOrThrow = (method: string, path: string, body: unknown, e: unknown): never => {
  if (
    shouldQueue(method, path) &&
    (isNetwork(e) || (e instanceof ApiFailure && isRetriableHttp(e.status)))
  ) {
    useLink.getState().enqueue({ method: method as MutMethod, path, body });
    throw new OfflineQueued();
  }
  if (isNetwork(e) || (e instanceof ApiFailure && isRetriableHttp(e.status))) {
    useLink.getState().setOnline(false);
  }
  throw e;
};

const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
  try {
    const res = await fetchApi(path, {
      method,
      signal: method === 'GET' ? AbortSignal.timeout(30_000) : undefined,
      headers: body === undefined ? undefined : jsonHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
    const data = await parse<T>(res);
    if (!useLink.getState().online) useLink.getState().setOnline(true);
    return data;
  } catch (e) {
    return queueOrThrow(method, path, body, e);
  }
};

export const api = {
  get: <T>(path: string): Promise<T> => reads.get(path, () => request<T>('GET', path)),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string): Promise<T> => request<T>('DELETE', path),

  /** Файл разом з ім'ям із заголовка — коли його не зберігають, а передають далі. */
  async file(
    path: string,
    fallbackName: string,
    body?: unknown,
  ): Promise<{ blob: Blob; name: string }> {
    const res = await fetchApi(path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? undefined : jsonHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
    if (!res.ok) await parse(res);
    const cd = res.headers.get('content-disposition') ?? '';
    const match = /filename\*=UTF-8''([^;]+)/.exec(cd) ?? /filename="?([^";]+)"?/.exec(cd);
    return { blob: await res.blob(), name: match ? decodeURIComponent(match[1]!) : fallbackName };
  },

  /**
   * Одноразова підміна збереження: поки вона стоїть, `download` не кладе файл
   * на диск, а віддає його сюди — так «Редагувати» бере той самий документ, що й
   * «Завантажити», без окремого шляху для кожного виду.
   */
  divert: null as ((blob: Blob, name: string) => Promise<void>) | null,

  /** Завантаження файлу з коректним ім'ям із заголовка. */
  async download(path: string, fallbackName: string, body?: unknown): Promise<void> {
    const { blob, name } = await this.file(path, fallbackName, body);
    const divert = this.divert;
    if (divert) {
      this.divert = null;
      await divert(blob, name);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /** Файл у пам'ять — для перегляду документа просто на сторінці. */
  async blob(path: string, body?: unknown): Promise<Blob> {
    return (await this.sheet(path, body)).blob;
  },

  /**
   * Аркуш перегляду: картинка сторінки й скільки сторінок у документі.
   * Кількість приходить заголовком `X-Pages` — саме з неї перегляд знає,
   * чи є куди гортати.
   */
  async sheet(path: string, body?: unknown): Promise<{ blob: Blob; pages: number; page: number }> {
    const res = await fetchApi(path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? undefined : jsonHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
    if (!res.ok) await parse(res);
    const num = (name: string, fallback: number): number => {
      const value = Number(res.headers.get(name));
      return Number.isFinite(value) && value > 0 ? value : fallback;
    };
    return { blob: await res.blob(), pages: num('x-pages', 1), page: num('x-page', 1) };
  },

  /** Сирі байти файлу (редактор документів): тип — octet-stream, не JSON. */
  async raw<T>(path: string, body: Blob): Promise<T> {
    const res = await fetchApi(path, {
      method: 'POST',
      headers: { 'content-type': 'application/octet-stream' },
      body,
      credentials: 'same-origin',
    });
    return parse<T>(res);
  },

  /** Сирі байти туди й файл назад (конвертація для друку). */
  async rawFile(path: string, body: Blob): Promise<Blob> {
    const res = await fetchApi(path, {
      method: 'POST',
      headers: { 'content-type': 'application/octet-stream' },
      body,
      credentials: 'same-origin',
    });
    if (!res.ok) await parse(res);
    return res.blob();
  },

  async upload<T>(path: string, form: FormData): Promise<T> {
    const res = await fetchApi(path, { method: 'POST', body: form, credentials: 'same-origin' });
    return parse<T>(res);
  },
};

export const errorText = (e: unknown): string =>
  e instanceof OfflineQueued || e instanceof ApiFailure
    ? e.message
    : e instanceof Error
      ? e.message
      : 'Невідома помилка';
